import type { ApolloCache } from '@apollo/client';
import { useMutation } from '@apollo/client';
import React, { useState } from 'react';
import { SessionActions } from '../../account/sessionSlice';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import Badge from '../../components/Badge';
import Button from '../../components/Button';
import Card, { Cards } from '../../components/Card';
import Form, { Field, Submit } from '../../components/Form';
import SettingCard from '../../settings/SettingCard';
import SettingsSection from '../../settings/SettingsSection';
import SettingsSectionUpsell from '../../settings/SettingsSectionUpsell';
import { UIActions } from '../../ui/uiSlice';
import getFeature from '../../utils/features';
import { updateQueryList } from '../../utils/graphql/updateQueryList';
import CustomDomain from './CustomDomain';
import styles from './CustomDomainSettings.module.scss';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import ConfirmationModal from '../../components/ConfirmationModal';
import Link from '../../utils/router/Link';

const CUSTOM_DOMAIN_SETTINGS_CUSTOM_DOMAINS_FRAGMENT = gql(`
  fragment CustomDomainSettings_customDomains on CustomDomain {
    active
    hostname
    ...CustomDomain_customDomain
  }
`);

type CustomDomainFragment = DocumentType<
  typeof CUSTOM_DOMAIN_SETTINGS_CUSTOM_DOMAINS_FRAGMENT
>;

export const CUSTOM_DOMAINS_QUERY = gql(`
  query customDomainsSettings {
    session: currentSession {
      organization {
        id
        customDomains {
          ...CustomDomainSettings_customDomains
        }
      }
    }
  }
`);

const CREATE_CUSTOM_DOMAIN_MUTATION = gql(`
  mutation createCustomDomain($hostname: String!) {
    customDomain: createCustomDomain(hostname: $hostname) {
      ...CustomDomainSettings_customDomains
    }
  }
`);

const DELETE_CUSTOM_DOMAIN_MUTATION = gql(`
  mutation deleteCustomDomain($hostname: ID!) {
    customDomain: deleteCustomDomain(hostname: $hostname) {
      hostname
    }
  }
`);

const VERIFY_CUSTOM_DOMAIN_MUTATION = gql(`
  mutation verifyCustomDomain($hostname: ID!) {
    customDomain: verifyCustomDomain(hostname: $hostname) {
      ...CustomDomainSettings_customDomains
    }
  }
`);

const SET_DEFAULT_CUSTOM_DOMAIN_MUTATION = gql(`
  mutation setDefaultCustomDomain($hostname: ID!) {
    organization: setDefaultCustomDomain(hostname: $hostname) {
      id
      effectiveHost
    }
  }
`);

const CLEAR_DEFAULT_CUSTOM_DOMAIN_MUTATION = gql(`
  mutation clearDefaultCustomDomain {
    organization: clearDefaultCustomDomain {
      id
      effectiveHost
    }
  }
`);

interface CustomDomainSettingsProps {
  customDomains: CustomDomainFragment[];
  loading: boolean;
  host: string;
  effectiveHost: string;
}

const CustomDomainSettings = ({
  customDomains,
  loading,
  host,
  effectiveHost,
}: CustomDomainSettingsProps) => {
  const isCustomDomainEnabled = getFeature('custom_domain');
  const dispatch = useAppDispatch();
  const [isAdding, setIsAdding] = useState(false);

  const [createCustomDomain] = useMutation(CREATE_CUSTOM_DOMAIN_MUTATION, {
    update: updateQueryList(
      { query: CUSTOM_DOMAINS_QUERY },
      'session.organization.customDomains',
      'customDomain',
      true,
    ),
  });

  const addCustomDomain = ({ hostname }: { hostname: string }) =>
    createCustomDomain({ variables: { hostname } }).then(() => {
      dispatch(
        UIActions.pushToast({
          level: 'success',
          message:
            'Custom domain added! It may take a several minutes to verify DNS and setup SSL so please check back later.',
        }),
      );
      setIsAdding(false);
    });

  const [deleteCustomDomain] = useMutation(DELETE_CUSTOM_DOMAIN_MUTATION, {
    update: updateQueryList(
      { query: CUSTOM_DOMAINS_QUERY },
      'session.organization.customDomains',
      'customDomain',
      false,
    ),
  });

  const removeCustomDomain = ({ active, hostname }: CustomDomainFragment) => {
    if (active) {
      dispatch(
        UIActions.openModal(ConfirmationModal, {
          title: 'Remove domain?',
          message: `Slab will stop serving all requests for ${hostname}`,
          onConfirm: () =>
            deleteCustomDomain({ variables: { hostname } }).then(() => {
              if (hostname === effectiveHost) {
                dispatch(
                  SessionActions.setCurrentOrganization({
                    effectiveHost: host,
                  }),
                );
              }
            }),
        }),
      );
    } else {
      deleteCustomDomain({ variables: { hostname } });
    }
  };

  const [verifyCustomDomain] = useMutation(VERIFY_CUSTOM_DOMAIN_MUTATION);

  const refreshCustomDomain = ({ hostname }: CustomDomainFragment) =>
    verifyCustomDomain({ variables: { hostname } }).then((res) => {
      if (!res?.data?.customDomain.active) {
        dispatch(
          UIActions.pushToast({
            level: 'warning',
            message:
              'DNS verification and SSL setup is still pending. Please check again later.',
          }),
        );
      }
    });

  const updateOrganizationCache = (
    cache: ApolloCache<unknown>,
    response?: {
      data?: {
        organization?: {
          id: string;
        } | null;
      } | null;
    } | null,
  ) => {
    try {
      const previousData = cache.readQuery({ query: CUSTOM_DOMAINS_QUERY });
      const prevOrg = previousData?.session?.organization;
      const nextOrg = response?.data?.organization;
      if (prevOrg && nextOrg && prevOrg.id === nextOrg.id) {
        cache.writeQuery({
          query: CUSTOM_DOMAINS_QUERY,
          data: {
            session: {
              ...previousData.session,
              organization: { ...prevOrg, ...nextOrg },
            },
          },
        });
      }
    } catch (e) {
      // readQuery failed
    }
  };

  const [setDefaultCustomDomain] = useMutation(
    SET_DEFAULT_CUSTOM_DOMAIN_MUTATION,
    {
      update: updateOrganizationCache,
    },
  );

  const [clearDefaultCustomDomain] = useMutation(
    CLEAR_DEFAULT_CUSTOM_DOMAIN_MUTATION,
    {
      update: updateOrganizationCache,
    },
  );

  const changeDefaultCustomDomain = (
    { hostname }: CustomDomainFragment,
    isDefault: boolean,
  ) => {
    if (isDefault) {
      setDefaultCustomDomain({
        variables: { hostname },
      }).then(
        (res) =>
          res?.data &&
          dispatch(
            SessionActions.setCurrentOrganization(res.data.organization),
          ),
      );
    } else {
      clearDefaultCustomDomain().then(
        (res) =>
          res.data &&
          dispatch(
            SessionActions.setCurrentOrganization(res.data.organization),
          ),
      );
    }
  };

  if (!isCustomDomainEnabled) {
    return (
      <SettingsSectionUpsell
        header="Custom Domains"
        feature="customDomains"
        description="Upgrade to set up a custom domain to use with your Slab account."
        contentCards={
          <Card>
            learn.yourcompany.com <Badge color="green">LIVE</Badge>
          </Card>
        }
      />
    );
  }

  return (
    <SettingsSection
      header="Custom Domains"
      subheader={
        !loading &&
        effectiveHost !== host && (
          <>
            Your Slab is also available at{' '}
            <Link to={`https://${host}`}>{host}</Link>.
          </>
        )
      }
    >
      {!loading && !isAdding && customDomains.length === 0 && (
        <div className={styles.empty}>None added</div>
      )}
      {(isAdding || customDomains.length > 0) && (
        <Cards>
          {customDomains.map((customDomain) => (
            <CustomDomain
              key={customDomain.hostname}
              customDomain={customDomain}
              isDefault={customDomain.hostname === effectiveHost}
              onChangeDefault={(isDefault) =>
                changeDefaultCustomDomain(customDomain, isDefault)
              }
              onRemove={() => removeCustomDomain(customDomain)}
              onRefresh={() => refreshCustomDomain(customDomain)}
            />
          ))}
          {isAdding && (
            <SettingCard
              description={
                <Form<{ hostname: string }>
                  initialValues={{ hostname: '' }}
                  onSubmit={addCustomDomain}
                  validateOnBlur={false}
                  validateOnChange={false}
                >
                  <div className={styles.fieldContainer}>
                    <Field
                      className={styles.field}
                      type="text"
                      id="hostname"
                      margin="none"
                      name="hostname"
                      placeholder="www.example.com"
                    />
                    <div className={styles.button}>
                      <Submit variant="primary">Save</Submit>
                    </div>
                    <div className={styles.button}>
                      <Button
                        variant="secondary"
                        onClick={() => setIsAdding(false)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </Form>
              }
              title={<label htmlFor="hostname">Add a domain</label>}
            />
          )}
        </Cards>
      )}
      <div className={styles.add}>
        {!isAdding && (
          <Button variant="secondary" onClick={() => setIsAdding(true)}>
            Add
          </Button>
        )}
        <div className={styles.instructions}>
          <Link
            to="https://go.slab.com/create-a-custom-domain-cname"
            target="_blank"
            rel="noopener noreferrer"
          >
            DNS configuration instructions
          </Link>
        </div>
      </div>
    </SettingsSection>
  );
};

export default CustomDomainSettings;
